/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DataPoint, DataSeries } from 'c3';
import * as React from 'react';
import { BasicChart } from 'src/core/components/BasicChart';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { ChartDataProps, ConfusionMatrixYAxis } from 'src/core/types/chart';
import { transformConfusionMatrixChartData } from 'src/core/utils/chartDataTransforms';

export type ConfusionMatrixChartProps = ChartDataProps<number, ConfusionMatrixYAxis> & {
    /**
     * Callback that fires when a point is clicked.
     */
    onPointClick?: (point: ConfusionMatrixYAxis) => void;
};

const confusionMatrixChartTheme = getScopedTheme('ConfusionMatrixChart');

/**
 * The confusion matrix chart component.
 */
export const ConfusionMatrixChart = (props: ConfusionMatrixChartProps) => {
    const { xData, yData, onPointClick } = props;
    const maxAbsXData: number = xData.data.length === 0 ? 0 : (Math.max.apply(null, xData.data.map(Math.abs)) as number);

    /**
     * A function invoked with the point data that was clicked on.
     */
    const onClick = (dataPoint: { x: number; value: number }) => {
        onPointClick({ id: null, xValue: dataPoint.x, score: dataPoint.value });
    };

    const configs: c3.ChartConfiguration = {
        data: {
            xs: { [yData.label]: xData.label },
            x: xData.label,
            columns: transformConfusionMatrixChartData(xData, yData),
            labels: false,
            type: 'scatter',
            onclick: onClick,
            color: generateColor,
            selection: {
                enabled: true,
                multiple: false
            }
        },

        axis: {
            x: {
                min: maxAbsXData * -1,
                max: maxAbsXData,
                tick: {
                    outer: false,
                    values: range(maxAbsXData * -1, maxAbsXData, 1),
                    count: 20,
                    format: () => ''
                }
            },
            y: {
                min: -1,
                max: 1
            }
        },

        grid: {
            x: {
                lines: [{ value: 0 }]
            },
            y: {
                show: true,
                lines: [{ value: 0 }]
            }
        },

        tooltip: {
            format: {
                title: () => '',
                name: () => 'Score',
                value: value => value as string
            }
        },

        point: {
            r: 5
        },
        zoom: {
            enabled: true
        },
        size: { height: 400 }
    };

    return <BasicChart {...configs} />;
};

/**
 * Generates an array starting and ending from the given params with the specified step
 */
const range = (start: number, end: number, step: number): number[] => {
    const values: number[] = [];
    for (let i: number = start; i < end; i += step) {
        values.push(i);
    }

    return values;
};

/**
 * Color generation function accepted by c3
 */
const generateColor = (_: Object, dataPoint: string | DataSeries | DataPoint): string => {
    if ((dataPoint as DataPoint).x === undefined) {
        return;
    }

    return (dataPoint as DataPoint).x * (dataPoint as DataPoint).value > 0
        ? confusionMatrixChartTheme.redPoint
        : confusionMatrixChartTheme.greenPoint;
};
